import React from "react";
import AnimationRevealPage from "../../../../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import Header from "../../../../Components/headers/PageHeader";
import MainFeature1 from "../../../../Components/Features/TwoColWithButton.js";
import Features from "../../../../Components/Features/ThreeColumnPages";
import {
  FaVideo,
  FaStar,
  FaRocket,
  FaEye,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";

// Styled Components
const Subheading = tw.span`uppercase tracking-wider text-sm`;
const Heading = tw.h1`text-2xl md:text-4xl lg:text-5xl font-bold`;
const Description = tw.p`text-base md:text-lg lg:text-xl`;

export const AnimationVideoProductionInDubai = () => {
  return (
    <AnimationRevealPage>
      {/* Header Section */}
      <Header from="Animation Video Production in Dubai" />

      {/* Hero Section */}
      <MainFeature1
        subheading={<Subheading>Bring Your Ideas to Life</Subheading>}
        heading={
          <Heading>
            Animation <span tw="text-primary-500">Video Production</span> in
            Dubai
          </Heading>
        }
        description={
          <Description>
            At MilaajBrandset, we deliver professional animation video
            production in **Dubai**, creating captivating 2D, 3D, and motion
            graphics videos that engage audiences and elevate brands. From
            explainer videos to animated ads, our animations drive impact and
            clarity.
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1633412802994-5c058f151b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Animation workspace
          "https://images.unsplash.com/photo-1618004912476-29818d81ae2e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // 3D animation concept
          "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Digital artwork
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Start Your Animation"
        buttonRounded={true}
        textOnLeft={false}
      />

      {/* Key Benefits Section */}
      <Features
        subheading={<Subheading>Why Choose Us?</Subheading>}
        heading="Expert Animation Video Production in Dubai"
        description="We craft animations that captivate and communicate."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Stunning Visuals",
            description: "High-quality 2D/3D animations and motion graphics.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Trusted Expertise",
            description: "Proven success with diverse industries.",
          },
          {
            icon: <FaRocket color="#ff5464" size={40} />,
            title: "Fast Delivery",
            description: "Timely production for urgent campaigns.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Engaging Storytelling",
            description: "Animations that simplify and captivate.",
          },
          {
            icon: <FaUsers color="#ff5464" size={40} />,
            title: "Custom Solutions",
            description: "Tailored to your brand and audience.",
          },
          {
            icon: <FaCheckCircle color="#ff5464" size={40} />,
            title: "Versatile Formats",
            description: "Explainers, ads, and social media content.",
          },
        ]}
      />

      {/* Our Process Section */}
      <MainFeature1
        subheading={<Subheading>How We Work</Subheading>}
        heading="Our Animation Video Production Process"
        description={
          <Description>
            We create impactful animations with a streamlined process:
            <ul tw="mt-4 list-disc pl-6">
              <li>Consultation: Understand your goals and vision.</li>
              <li>Storyboarding: Develop concepts and visual scripts.</li>
              <li>Design: Create characters, scenes, and assets.</li>
              <li>Animation: Bring visuals to life with 2D/3D techniques.</li>
              <li>Editing: Enhance with sound design and effects.</li>
              <li>Delivery: Provide polished videos with revisions.</li>
            </ul>
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1628348070889-cb656235b4eb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Digital illustration
          "https://images.unsplash.com/photo-1633412802994-5c058f151b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Animation workspace
          "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // 3D modeling screen
        ]}
        widths={["60%", "55%", "70%"]}
        heights={["60%", "45%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "right: 30%; top: 10%;",
          "left: 35%; bottom: 20%;",
          "right: 32%; bottom: 10%;",
        ]}
        primaryButtonText="Learn More"
        buttonRounded={true}
        textOnLeft={true}
      />

      {/* Success Stories Section */}
      <Features
        subheading={<Subheading>Success Stories</Subheading>}
        heading="Animation Video Wins in Dubai"
        description="Our animations have transformed brand messaging."
        cards={[
          {
            icon: <FaVideo color="#ff5464" size={40} />,
            title: "Explainer Success",
            description: "Boosted conversions with a clear animated video.",
          },
          {
            icon: <FaEye color="#ff5464" size={40} />,
            title: "Audience Reach",
            description: "Increased engagement for a Dubai startup.",
          },
          {
            icon: <FaStar color="#ff5464" size={40} />,
            title: "Client Satisfaction",
            description: "96% approval from animation clients.",
          },
        ]}
      />

      {/* Call-to-Action Section */}
      <MainFeature1
        subheading={<Subheading>Ready for Animated Impact?</Subheading>}
        heading="Animation Video Production in Dubai"
        description={
          <Description>
            Partner with MilaajBrandset for professional animation video
            production in Dubai. Get engaging, high-quality animations that
            elevate your brand—contact us today!
          </Description>
        }
        imageSrcs={[
          "https://images.unsplash.com/photo-1648387598225-1a44ef324bee?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Creative design workspace
          "https://images.unsplash.com/photo-1579403124614-197f69d8187b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Digital animation concept
          "https://images.unsplash.com/photo-1626785774625-6babb4418d56?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80", // Animation monitor setup
        ]}
        widths={["55%", "60%", "70%"]}
        heights={["45%", "60%", "35%"]}
        imageColumnStyle="height: 400px;"
        imageStyle="width: 45%; height: 60%;"
        imagePositions={[
          "left: 35%; top: 20%;",
          "right: 30%; top: 10%;",
          "left: 32%; bottom: 20%;",
        ]}
        primaryButtonText="Contact Us Now"
        buttonRounded={true}
        textOnLeft={false}
      />
    </AnimationRevealPage>
  );
};
